import classNames from 'classnames'
import React from 'react'

import {
  Delayed,
  DialogBody,
  Popover,
  PopoverTrigger,
  Spinner,
  ZIndexContext,
  getExtendedDialogState,
  usePopoverState,
} from '../../components'
import { ChartEventBadgeType } from '../../constants/common'
import { chartEventsAsyncModule } from '../../controllers/chart-events/async-modules'
import { useModelState } from '../../model-hooks/use-model-state'
import ChartModel from '../../models/chart'
import { useAsyncModule } from '../../utils/async-modules-utils'
import { useClickAway } from '../../utils/use-click-away'

interface ChartEventPopoverWithStateProps {
  chartModel: ChartModel
}

const POPOVER_ID = 'badge-popover'

export function ChartEventPopoverWithState({ chartModel }: ChartEventPopoverWithStateProps) {
  const unwatchedChartLayout = React.useMemo(() => chartModel.chart_layout() ?? null, [chartModel])
  const { activeChartEvent, theme } = useModelState(unwatchedChartLayout, { watchProperties: ['activeChartEvent'] })
  const popoverState = usePopoverState({ placement: 'top' })
  const { isFullyClosed } = getExtendedDialogState(popoverState)
  const [style, setStyle] = React.useState({
    top: 0,
    left: 0,
  })
  const [chartEventsModule] = useAsyncModule({
    ...chartEventsAsyncModule,
    shouldLoadModule: !isFullyClosed,
    errorSeverity: 'medium',
    onError: () => popoverState.hide(),
  })
  const elementId = activeChartEvent?.elementId
  const isChartEventOfThisChart = activeChartEvent?.pane().chart().eql(chartModel)
  const [data, setData] = React.useState<ObjectHash | null>(null)
  const clickAwayRef = useClickAway(() => {
    const isAnyBadgeHovered = !!chartModel
      .getChartPane()
      ?.getAllChartEvents()
      .some((chartEvent) => chartEvent.instance.isHovered)

    if (!isAnyBadgeHovered && activeChartEvent && isChartEventOfThisChart) {
      activeChartEvent.instance.toggleIsOpen(false)
    }
  }, !!activeChartEvent && isChartEventOfThisChart)
  // This is intentional, it doesn't need to be in useEffect, it would just call effect function which is redundant
  clickAwayRef.current = popoverState.unstable_popoverRef.current
  const popoverShow = popoverState.show
  const popoverHide = popoverState.hide

  React.useEffect(() => {
    if (!activeChartEvent?.instance.isOpen || !isChartEventOfThisChart) {
      if (isFullyClosed) {
        return setData(null)
      }
      return popoverHide()
    }
    if (!isChartEventOfThisChart) return
    const { top, left } = activeChartEvent.instance
    const newData = activeChartEvent.getChartEventData()
    if (newData?.elementId !== data?.elementId) {
      setData({
        ...newData,
        badgeType: activeChartEvent.instance.getChartEvenBadgeType(),
      })
    }

    if (style.top !== top || style.left !== left) {
      popoverHide()
      setStyle({
        top,
        left,
      })
    }
    if (!popoverState.visible) {
      popoverShow()
    }
  }, [
    elementId,
    isFullyClosed,
    data,
    style,
    popoverShow,
    popoverHide,
    popoverState.visible,
    activeChartEvent,
    chartModel,
    isChartEventOfThisChart,
  ])

  return (
    <>
      <PopoverTrigger as="div" state={popoverState} className="absolute" style={style} />
      {data && !isFullyClosed && (
        <ZIndexContext.Provider value="z-dialog">
          <Popover
            id={POPOVER_ID}
            tabIndex={0}
            hasBackdrop={false}
            aria-label="Chart Settings"
            className={classNames('relative z-10 mt-10 flex max-h-full max-w-full flex-col', {
              'min-h-48': [
                ChartEventBadgeType.EarningsNeutral,
                ChartEventBadgeType.EarningsFuture,
                ChartEventBadgeType.EarningsNegative,
                ChartEventBadgeType.EarningsPositive,
              ].includes(data.badgeType),
              'min-h-28': data.badgeType === ChartEventBadgeType.Dividends,
              'min-h-20': data.badgeType === ChartEventBadgeType.Split,
            })}
            state={popoverState}
            focusOnHide={false}
            focusOnShow={false}
            hideOnClickOutside={false}
            hasViewportHeightLimit
            data-testid="chart-event-popover"
          >
            <DialogBody
              className={classNames('select-text', {
                'flex flex-1 ': !chartEventsModule,
              })}
            >
              {chartEventsModule ? (
                <chartEventsModule.ChartEventPopoverContent badgeType={data.badgeType} data={data} theme={theme} />
              ) : (
                <div className="flex min-w-36 items-center justify-center">
                  <Delayed delayComponent={null}>
                    <Spinner width={24} />
                  </Delayed>
                </div>
              )}
            </DialogBody>
          </Popover>
        </ZIndexContext.Provider>
      )}
    </>
  )
}
